import React, { useState } from 'react';
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, MenuItem, Paper, Stack, Switch, TextField, Typography,
} from '@mui/material';
import { request, useOperations } from './OperationsProvider';
import FormRow from '../common/components/FormRow';
import { formatDateTime } from '../common/util/datetime';

// Reports that go out on their own, to the people who read them.
//
// The four cadences are the four moments a plant looks at its figures: the end of a shift, the start
// of a day, the Monday review and the monthly bill. Each schedule says which report, for which of
// those periods, to whom, and in what form - and then nobody signs in to produce it again.
const REPORTS = [
  ['trips', 'Trip detail'],
  ['turnaround', 'Turnaround (slowest first)'],
  ['checkpoints', 'Checkpoint segments'],
  ['billing', 'Billable trips (daily)'],
  ['billing-detail', 'Billable trips (itemised)'],
  ['exceptions', 'Exception history'],
];
const CADENCES = [
  ['SHIFT', 'End of every shift'],
  ['DAILY', 'Daily'],
  ['WEEKLY', 'Weekly'],
  ['MONTHLY', 'Monthly'],
];
const FORMATS = [['xlsx', 'Excel'], ['pdf', 'PDF'], ['csv', 'CSV']];
const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const blank = {
  name: '', report: 'trips', cadence: 'DAILY', format: 'xlsx', recipients: '', atHour: 6, weekday: 1, dayOfMonth: 1, direction: '', transporter: '', material: '', groupByShift: false,
};

const when = (schedule) => {
  const at = `${String(schedule.atHour ?? 6).padStart(2, '0')}:${String(schedule.atMinute ?? 0).padStart(2, '0')}`;
  if (schedule.cadence === 'SHIFT') return 'at the end of every shift';
  if (schedule.cadence === 'WEEKLY') return `every ${DAYS[schedule.weekday ?? 1]} at ${at}`;
  if (schedule.cadence === 'MONTHLY') return `on the ${schedule.dayOfMonth ?? 1}${(schedule.dayOfMonth ?? 1) === 1 ? 'st' : 'th'} of each month at ${at}`;
  return `every day at ${at}`;
};

const SchedulePanel = () => {
  const { data, refresh } = useOperations();
  const [form, setForm] = useState(null);
  const [editing, setEditing] = useState(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [pending, setPending] = useState(false);
  const rows = data.reportSchedules || [];
  const set = (key, value) => setForm((current) => ({ ...current, [key]: value }));

  const open = (schedule) => {
    setEditing(schedule || null);
    setForm(schedule
      ? { ...blank, ...schedule, groupByShift: schedule.groupByShift === 'true' || schedule.groupByShift === true, recipients: (schedule.recipients || []).join(', ') }
      : { ...blank });
    setError('');
  };

  const save = async (event) => {
    event.preventDefault();
    setPending(true); setError('');
    try {
      const body = {
        ...form,
        recipients: form.recipients.split(/[,;\s]+/).filter(Boolean),
        atHour: Number(form.atHour),
        weekday: Number(form.weekday),
        dayOfMonth: Number(form.dayOfMonth),
        // The server keeps its flags as strings, as it does for enabled.
        groupByShift: form.groupByShift ? 'true' : 'false',
      };
      if (editing) await request(`/api/operations/report-schedules/${editing.id}`, 'PATCH', body);
      else await request('/api/operations/reportSchedules', 'POST', body);
      await refresh();
      setForm(null); setEditing(null);
      setNotice(editing ? 'Schedule updated.' : 'Schedule added.');
    } catch (e) { setError(e.message); } finally { setPending(false); }
  };

  const act = async (schedule, what) => {
    setPending(true); setError(''); setNotice('');
    try {
      if (what === 'delete') await request(`/api/operations/report-schedules/${schedule.id}`, 'DELETE');
      else if (what === 'send') {
        await request(`/api/operations/report-schedules/${schedule.id}/send`, 'POST', {});
        setNotice(`${schedule.name} sent to ${(schedule.recipients || []).join(', ')}.`);
      } else await request(`/api/operations/report-schedules/${schedule.id}`, 'PATCH', { enabled: what === 'enable' ? 'true' : 'false' });
      await refresh();
    } catch (e) { setError(e.message); } finally { setPending(false); }
  };

  return (
    <Stack gap={2}>
      <Stack direction="row" alignItems="flex-start" gap={2} flexWrap="wrap">
        <Box sx={{ flex: 1, minWidth: 260 }}>
          <Typography variant="body2" color="text.secondary">
            Reports sent from the plant&apos;s own mail account, for the period they cover. Shift reports follow the shift times set under Operational rules.
          </Typography>
        </Box>
        <Button variant="contained" onClick={() => open(null)}>Add schedule</Button>
      </Stack>
      {error && <Alert severity="error">{error}</Alert>}
      {notice && <Alert severity="success" onClose={() => setNotice('')}>{notice}</Alert>}
      {!rows.length && (
        <Paper variant="outlined" sx={{ p: 5, textAlign: 'center' }}>
          <Typography variant="h6">No reports are scheduled</Typography>
          <Typography color="text.secondary">Add one, and it will arrive at the end of each shift, day, week or month.</Typography>
        </Paper>
      )}
      {rows.map((schedule) => (
        <Paper key={schedule.id} variant="outlined" sx={{ p: 2.5 }}>
          <Stack direction="row" justifyContent="space-between" gap={2} flexWrap="wrap">
            <Box sx={{ minWidth: 0 }}>
              <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
                <Typography fontWeight={600}>{schedule.name}</Typography>
                <Chip size="small" label={schedule.enabled ? 'On' : 'Paused'} color={schedule.enabled ? 'success' : 'default'} variant={schedule.enabled ? 'filled' : 'outlined'} />
                <Chip size="small" variant="outlined" label={FORMATS.find(([value]) => value === schedule.format)?.[1] || 'Excel'} />
              </Stack>
              <Typography variant="body2" color="text.secondary">
                {`${REPORTS.find(([value]) => value === schedule.report)?.[1] || schedule.report} · ${when(schedule)}`}
              </Typography>
              <Typography variant="body2">{(schedule.recipients || []).join(', ')}</Typography>
              {[['Transporter', schedule.transporter], ['Material', schedule.material], ['Direction', schedule.direction], ['Site', schedule.site]].some(([, value]) => value) && (
                <Typography variant="caption" color="text.secondary">
                  {[['Transporter', schedule.transporter], ['Material', schedule.material], ['Direction', schedule.direction], ['Site', schedule.site]].filter(([, value]) => value).map(([label, value]) => `${label}: ${value}`).join(' · ')}
                </Typography>
              )}
              {schedule.lastRunAt && (
                <Typography variant="caption" color={schedule.lastStatus === 'FAILED' ? 'error.main' : 'text.secondary'} display="block">
                  {`Last run ${formatDateTime(schedule.lastRunAt)} · ${schedule.lastStatus === 'SENT' ? `sent ${schedule.lastRecords} records` : schedule.lastStatus === 'SKIPPED' ? 'skipped' : 'failed'}${schedule.lastDetail ? ` · ${schedule.lastDetail}` : ''}`}
                </Typography>
              )}
            </Box>
            <Stack direction="row" gap={1} alignItems="flex-start" flexWrap="wrap">
              <Button size="small" disabled={pending} onClick={() => act(schedule, 'send')}>Send now</Button>
              <Button size="small" disabled={pending} onClick={() => open(schedule)}>Edit</Button>
              <Button size="small" disabled={pending} onClick={() => act(schedule, schedule.enabled ? 'pause' : 'enable')}>{schedule.enabled ? 'Pause' : 'Resume'}</Button>
              <Button size="small" color="error" disabled={pending} onClick={() => act(schedule, 'delete')}>Delete</Button>
            </Stack>
          </Stack>
        </Paper>
      ))}

      <Dialog open={!!form} onClose={() => !pending && setForm(null)} fullWidth maxWidth="sm">
        <Box component="form" onSubmit={save}>
          <DialogTitle>{editing ? 'Edit schedule' : 'Schedule a report'}</DialogTitle>
          <DialogContent>
            <Stack gap={2} sx={{ pt: 1 }}>
              {error && <Alert severity="error">{error}</Alert>}
              <TextField label="Name" value={form?.name || ''} disabled={pending} onChange={(e) => set('name', e.target.value)} helperText="Optional; names itself if left empty" />
              <FormRow>
                <TextField select required label="Report" value={form?.report || 'trips'} disabled={pending} onChange={(e) => set('report', e.target.value)}>
                  {REPORTS.map(([value, name]) => <MenuItem key={value} value={value}>{name}</MenuItem>)}
                </TextField>
                <TextField select required label="How often" value={form?.cadence || 'DAILY'} disabled={pending} onChange={(e) => set('cadence', e.target.value)}>
                  {CADENCES.map(([value, name]) => <MenuItem key={value} value={value}>{name}</MenuItem>)}
                </TextField>
              </FormRow>
              <FormRow>
                {form?.cadence === 'SHIFT' ? (
                  <TextField disabled label="When" value="At each shift change" helperText="Set the shift times under Operational rules" />
                ) : (
                  <TextField select label="Time of day" value={form?.atHour ?? 6} disabled={pending} onChange={(e) => set('atHour', e.target.value)}>
                    {Array.from({ length: 24 }, (unused, hour) => <MenuItem key={hour} value={hour}>{`${String(hour).padStart(2, '0')}:00`}</MenuItem>)}
                  </TextField>
                )}
                {form?.cadence === 'WEEKLY' && (
                  <TextField select label="Day of the week" value={form?.weekday ?? 1} disabled={pending} onChange={(e) => set('weekday', e.target.value)}>
                    {DAYS.map((name, index) => <MenuItem key={name} value={index}>{name}</MenuItem>)}
                  </TextField>
                )}
                {form?.cadence === 'MONTHLY' && (
                  <TextField select label="Day of the month" value={form?.dayOfMonth ?? 1} disabled={pending} onChange={(e) => set('dayOfMonth', e.target.value)}>
                    {Array.from({ length: 28 }, (unused, index) => <MenuItem key={index + 1} value={index + 1}>{index + 1}</MenuItem>)}
                  </TextField>
                )}
                {['DAILY', 'SHIFT'].includes(form?.cadence) && (
                  <TextField select label="Format" value={form?.format || 'xlsx'} disabled={pending} onChange={(e) => set('format', e.target.value)}>
                    {FORMATS.map(([value, name]) => <MenuItem key={value} value={value}>{name}</MenuItem>)}
                  </TextField>
                )}
              </FormRow>
              {!['DAILY', 'SHIFT'].includes(form?.cadence) && (
                <TextField select label="Format" value={form?.format || 'xlsx'} disabled={pending} onChange={(e) => set('format', e.target.value)} sx={{ maxWidth: 220 }}>
                  {FORMATS.map(([value, name]) => <MenuItem key={value} value={value}>{name}</MenuItem>)}
                </TextField>
              )}
              {/* A plant that runs shifts reads its day in shifts. A shift-scheduled report already
                  covers one shift, so this is for the reports that span several. */}
              {form?.cadence !== 'SHIFT' && (
                <FormControlLabel
                  control={<Switch checked={!!form?.groupByShift} disabled={pending} onChange={(e) => set('groupByShift', e.target.checked)} />}
                  label={<Typography variant="body2">Group the report by shift</Typography>}
                />
              )}
              <TextField
                required
                multiline
                minRows={2}
                label="Send to"
                value={form?.recipients || ''}
                disabled={pending}
                onChange={(e) => set('recipients', e.target.value)}
                helperText="Email addresses, separated by commas"
              />
              <Typography variant="subtitle2">Narrow it (optional)</Typography>
              <FormRow columns="1fr 1fr 1fr">
                <TextField select label="Direction" value={form?.direction || ''} disabled={pending} onChange={(e) => set('direction', e.target.value)}>
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="INBOUND">Inbound</MenuItem>
                  <MenuItem value="OUTBOUND">Outbound</MenuItem>
                </TextField>
                <TextField select label="Transporter" value={form?.transporter || ''} disabled={pending} onChange={(e) => set('transporter', e.target.value)}>
                  <MenuItem value="">All</MenuItem>
                  {[...new Set((data.catalog || []).filter((c) => c.list === 'transporter').map((c) => c.name))].map((name) => <MenuItem key={name} value={name}>{name}</MenuItem>)}
                </TextField>
                <TextField select label="Material" value={form?.material || ''} disabled={pending} onChange={(e) => set('material', e.target.value)}>
                  <MenuItem value="">All</MenuItem>
                  {[...new Set((data.catalog || []).filter((c) => c.list === 'material').map((c) => c.name))].map((name) => <MenuItem key={name} value={name}>{name}</MenuItem>)}
                </TextField>
              </FormRow>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button disabled={pending} onClick={() => setForm(null)}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={pending}>{pending ? 'Saving…' : 'Save schedule'}</Button>
          </DialogActions>
        </Box>
      </Dialog>
    </Stack>
  );
};

export default SchedulePanel;
